// src/shared/upload.js — качване на СУРОВ файл към сървъра (доставки, POS).
// И ЕДИННАТА зона за пускане на файл (dropZone) — „Доставки" и „Заплати".
//
// Защо не api(): api() праща JSON, а оригиналът трябва да стигне байт в байт
// (сървърът проверява съдържанието и смята sha256). Името е в заглавка,
// кодирано — кирилица в HTTP заглавка иначе се чупи.
// Грешката носи status и пълното тяло (err.data): при 409 екраните четат
// оттам { exists } / { draft } / { duplicate }, не гадаят по текста.

import { el } from './dom.js';

// Какво вижда КЛИЕНТЪТ след качване — в „Доставки" и в „Качи от телефона".
// „качен ✓" и „OCR не е настроен" са за кантората; клиентът трябва да знае
// само, че е готово и няма какво още да прави (ОТЧЕТ-ПРОМТ-38, Д6).
export const CLIENT_DONE_TEXT = 'Качено. Кантората ще го обработи — не е нужно да правите нищо повече.';

export async function uploadRaw(url, file, opts = {}) {
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': opts.contentType || file.type || 'application/octet-stream', 'X-Original-Name': encodeURIComponent(file.name) },
      body: file,
    });
  } catch (e) {
    throw new Error('Няма връзка със сървъра — проверете интернета и опитайте пак.');
  }
  let data = {};
  try { data = await res.json(); } catch (e) {}
  if (!res.ok) {
    const err = new Error(data.error || (res.status === 413 ? 'Файлът е над 20 MB.' : res.status === 429 ? 'Твърде много качвания — изчакайте малко.' : 'Файлът не се качи.'));
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Същото като uploadRaw, но с напредък (ПРОМТ-37, „Качи от телефона"):
// fetch не казва колко е изпратено, XMLHttpRequest — да. onProgress(0..1).
// Грешката е със същия вид (status, data), за да се чете еднакво.
export function uploadRawProgress(url, blob, name, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', blob.type || 'application/octet-stream');
    xhr.setRequestHeader('X-Original-Name', encodeURIComponent(name));
    if (xhr.upload && onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable && e.total) onProgress(e.loaded / e.total); };
    xhr.onerror = () => reject(new Error('Връзката прекъсна — проверете интернета и натиснете „Опитай пак“.'));
    xhr.ontimeout = xhr.onerror;
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText || '{}'); } catch (e) {}
      if (xhr.status >= 200 && xhr.status < 300) { if (onProgress) onProgress(1); resolve(data); return; }
      const err = new Error(data.error || (xhr.status === 413 ? 'Файлът е над 20 MB.' : xhr.status === 429 ? 'Твърде много качвания — изчакайте малко.' : 'Файлът не се качи.'));
      err.status = xhr.status;
      err.data = data;
      reject(err);
    };
    xhr.send(blob);
  });
}

// Зона за пускане на файл: пунктирана рамка, заглавие, бутон „Избери файл"
// и подсказка. Голото поле на браузъра е скрито (sr-only) — то пише „Choose
// File / No file chosen" на английски и не приема пускане с мишката. Бутонът
// е <label> към полето: Enter/интервал го отварят и от клавиатурата.
// Изнесена от „Доставки" (стъпка 8), за да е ЕДНА и съща и в „Заплати".
//   id       — id на скритото поле (уникално на екрана)
//   onFiles  — (FileList | File[]) => …; проверката на вида е на екрана
export function dropZone({ id, title, hint, accept, multiple, ariaLabel, onFiles }) {
  const hintId = id + '-hint';
  const inp = el('input', { type: 'file', accept, multiple: multiple ? 'multiple' : null, class: 'sr-only', id, 'aria-describedby': hint ? hintId : null });
  inp.addEventListener('change', async () => { await onFiles(inp.files); inp.value = ''; });
  const zone = el('div', { class: 'drop-zone', role: 'group', 'aria-label': ariaLabel || title },
    el('p', { class: 'drop-zone-title', text: title }),
    el('label', { class: 'btn', for: id, tabindex: '0', text: 'Избери файл',
      onKeydown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inp.click(); } } }),
    inp,
    hint ? el('p', { class: 'drop-zone-hint', id: hintId, text: hint }) : null);
  zone.addEventListener('dragover', (e) => { e.preventDefault(); zone.classList.add('over'); });
  zone.addEventListener('dragleave', () => zone.classList.remove('over'));
  zone.addEventListener('drop', (e) => {
    e.preventDefault();
    zone.classList.remove('over');
    onFiles(e.dataTransfer && e.dataTransfer.files);
  });
  return zone;
}
